import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, KeyRoundIcon, Loader2Icon, ShieldIcon, UserXIcon } from 'lucide-react';
import { accessByProfile, profiles } from '../../data/offboarding';
import type { AccessItem, DeactivationResult, OrgUser } from '../../types/offboarding';
import { neutralButton, primaryButton } from '../../utils/buttonStyles';
import { Section } from './Section';
import { ToolCardHeader } from './ToolCardHeader';
interface AccessDeactivationProps {
  user: OrgUser;
  onBack: () => void;
  onContinue: (result: DeactivationResult) => void;
}
export function AccessDeactivation({
  user,
  onBack,
  onContinue
}: AccessDeactivationProps) {
  const access = accessByProfile[user.profileId] ?? {
    licenses: [],
    permissionSets: []
  };
  const [licenseIds, setLicenseIds] = useState(access.licenses.map(l => l.id));
  const [permissionSetIds, setPermissionSetIds] = useState(access.permissionSets.map(p => p.id));
  const [deactivateUser, setDeactivateUser] = useState(true);
  const [freezeLogin, setFreezeLogin] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<DeactivationResult | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const profileName = profiles.find(p => p.id === user.profileId)?.name;
  const nothingSelected = licenseIds.length === 0 && permissionSetIds.length === 0 && !deactivateUser && !freezeLogin;
  const run = () => {
    setIsRunning(true);
    timer.current = setTimeout(() => {
      setIsRunning(false);
      setResult({
        userId: user.id,
        licensesRemoved: licenseIds.length,
        permissionSetsRemoved: permissionSetIds.length,
        userDeactivated: deactivateUser,
        loginFrozen: freezeLogin
      });
    }, 1600);
  };
  return <section aria-label={`Deactivate access for ${user.name}`} className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-24" data-mp="1o8aos5sx251b">
      <ToolCardHeader subtitle="Remove access before reassigning records" data-id="element-25" data-mp="2du3oowsrrn57" />

      <div className="space-y-6 px-5 py-6 sm:px-6" data-id="element-26" data-mp="2hw8fzovqw7oo">
        {/* Departing user */}
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3" data-id="element-27" data-mp="2mpjhrblp9y9q">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-700 ring-1 ring-gray-200" data-id="element-28" data-mp="2rm9vhsa18hrz">
            {user.name.split(' ').map(p => p[0]).join('')}
          </span>
          <div className="min-w-0 flex-1" data-id="element-29" data-mp="1z0qpbp4ae8jv">
            <p className="truncate text-sm font-semibold text-gray-900" data-id="element-30" data-mp="23wz06qbgbfl8">{user.name}</p>
            <p className="truncate text-xs text-gray-500" data-id="element-31" data-mp="28xdayg4ft3my">
              {user.email}
              {profileName && <span className="hidden sm:inline" data-id="element-32" data-mp="3fdyq5fabtic3"> · {profileName}</span>}
            </p>
          </div>
          <span className={`flex-shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium ${user.active ? 'bg-success/10 text-success' : 'bg-gray-200 text-gray-600'}`} data-id="element-33" data-mp="3kbh09902y3lo">
            {user.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {result ? <div className="rounded-xl border border-success/25 bg-success/5 px-5 py-4" role="status" data-id="element-34" data-mp="yxixaydxmjb2">
            <div className="flex gap-3" data-id="element-35" data-mp="tzhducclghz2">
              <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" aria-hidden="true" data-id="element-36" data-mp="pyfe7uerm3wk" />
              <div className="text-sm" data-id="element-37" data-mp="kzlwtqwcxsr4">
                <p className="font-semibold text-gray-900" data-id="element-38" data-mp="kyjazxdw6gi2">Access deactivated for {user.name}.</p>
                <ul className="mt-1.5 space-y-0.5 text-gray-700" data-id="element-39" data-mp="g10y5vwr7h4s">
                  <li data-id="element-40" data-mp="b1oa57u5c40m">{result.licensesRemoved} package licenses removed</li>
                  <li data-id="element-41" data-mp="2aeaadvgopgqq">{result.permissionSetsRemoved} permission sets unassigned</li>
                  {result.loginFrozen && <li data-id="element-42" data-mp="25f6011i0xcyt">Login frozen</li>}
                  {result.userDeactivated && <li data-id="element-43" data-mp="25fyy7e2x2u5c">Salesforce user deactivated</li>}
                </ul>
                <p className="mt-3 text-gray-700" data-id="element-44" data-mp="2dh6p5e7ixine">
                  Next, reassign the {user.personAccounts} Person Accounts and {user.leads} Leads they own.
                </p>
              </div>
            </div>
          </div> : <>
            <Section number={1} title="Package Licenses" icon={KeyRoundIcon} aside={<ToggleAll items={access.licenses} selected={licenseIds} onChange={setLicenseIds} data-id="element-46" data-mp="1uocjxbzpd4wd" />} data-id="element-45" data-mp="28gimtwduhfdv">
              <AccessChecklist items={access.licenses} selected={licenseIds} onChange={setLicenseIds} data-id="element-47" data-mp="1zgvvk3etxpyj" />
            </Section>

            <Section number={2} title="Permission Sets" icon={ShieldIcon} aside={<ToggleAll items={access.permissionSets} selected={permissionSetIds} onChange={setPermissionSetIds} data-id="element-49" data-mp="1bpzyaqrlu2jw" />} data-id="element-48" data-mp="1kp3hduqruhye">
              <AccessChecklist items={access.permissionSets} selected={permissionSetIds} onChange={setPermissionSetIds} data-id="element-50" data-mp="x4ufuhqu24gc" />
            </Section>

            <Section number={3} title="User Record" icon={UserXIcon} data-id="element-51" data-mp="165kc1mk09wul">
              <div className="space-y-3" data-id="element-52" data-mp="114wjuxk1j9ys">
                <CheckRow checked={freezeLogin} onChange={() => setFreezeLogin(v => !v)} title="Freeze login immediately" detail="Blocks sign-in while access is being removed." data-id="element-53" data-mp="113ux3vtnwz18" />
                <CheckRow checked={deactivateUser} onChange={() => setDeactivateUser(v => !v)} title="Deactivate Salesforce user" detail="Frees the user license. Their records stay with them until you reassign them next." data-id="element-54" data-mp="1ov8pu2vq6wmq" />
              </div>
            </Section>

            <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3.5" data-id="element-55" data-mp="1ts8uqagu9tc9">
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden="true" data-id="element-56" data-mp="rekto43hrnw8" />
              <p className="text-[13px] text-gray-800" data-id="element-57" data-mp="vfwhq56pw12b">
                Removed licenses and permission sets take effect immediately and can only be restored
                manually.
              </p>
            </div>
          </>}
      </div>

      <footer className="flex flex-col gap-3 border-t border-gray-100 px-5 py-5 sm:px-6 md:flex-row md:items-center md:justify-end" data-id="element-58" data-mp="1a6djxqr6ev09">
        {result ? <button type="button" onClick={() => onContinue(result)} className={`${primaryButton} md:min-w-[120px]`} data-id="element-59" data-mp="1j7ttjwa62t3f">
            Continue to Reassignment
          </button> : <>
            <button type="button" onClick={onBack} disabled={isRunning} className={neutralButton} data-id="element-60" data-mp="3l0hzwei3xwnf">
              Back
            </button>
            <button type="button" onClick={run} disabled={nothingSelected || isRunning} className={`${primaryButton} md:min-w-[160px]`} data-id="element-61" data-mp="3q04d4nxxrcw8">
              {isRunning ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-62" data-mp="38pqrw3v1w9gk" />
                  Deactivating…
                </> : 'Deactivate Access'}
            </button>
          </>}
      </footer>
    </section>;
}
function AccessChecklist({
  items,
  selected,
  onChange
}: {
  items: AccessItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-500" data-id="element-63" data-mp="3neehzum4nqz4">Nothing assigned.</p>;
  }
  return <ul className="grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2" data-id="element-64" data-mp="2g0bg3bvkacjl">
      {items.map(item => <li key={item.id} data-id="element-65" data-mp="2bypun24yopxs">
          <CheckRow checked={selected.includes(item.id)} onChange={() => onChange(selected.includes(item.id) ? selected.filter(id => id !== item.id) : [...selected, item.id])} title={item.name} detail={item.detail} data-id="element-66" data-mp="2bygfyapda57e" />
        </li>)}
    </ul>;
}
function CheckRow({
  checked,
  onChange,
  title,
  detail
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  detail: string;
}) {
  return <label className="flex cursor-pointer items-start gap-2.5" data-id="element-67" data-mp="yofdi34j5dln">
      <input type="checkbox" checked={checked} onChange={onChange} className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-navy-600" data-id="element-68" data-mp="12n4yagrwdxfb" />
      <span data-id="element-69" data-mp="ttrsie8qj1bt">
        <span className="block text-sm text-gray-900" data-id="element-70" data-mp="tt8cx46k4p0y">{title}</span>
        <span className="block text-xs text-gray-500" data-id="element-71" data-mp="1hk5epo4n25yh">{detail}</span>
      </span>
    </label>;
}
function ToggleAll({
  items,
  selected,
  onChange
}: {
  items: AccessItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const allSelected = selected.length === items.length;
  return <button type="button" onClick={() => onChange(allSelected ? [] : items.map(i => i.id))} className="text-xs font-medium text-navy-600 hover:text-navy-800 hover:underline" data-id="element-72" data-mp="1rocdxkiyqs99">
      {allSelected ? 'Clear all' : 'Select all'}
    </button>;
}